import { Container, Graphics, Sprite, Text, TextStyle, Ticker, type Texture } from 'pixi.js';
import gsap from 'gsap';
import { COLORS, FONT, TIMING } from '../config/tokens';
import { frame } from '../assets/loader';
import type { TileKind } from '../core/types';

/** Palette colour that belongs to each tile (particles, flashes). */
export const KIND_COLOR: Record<TileKind, number> = {
  latte: COLORS.sky,
  croissant: COLORS.butter,
  cupcake: COLORS.blush,
  macaron: COLORS.lavender,
  donut: COLORS.sage,
  cookie: COLORS.peach,
};

export const reducedMotion = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;

interface Particle {
  s: Sprite;
  vx: number;
  vy: number;
  gravity: number;
  spin: number;
  drag: number;
  life: number;
  age: number;
  scale0: number;
  scale1: number;
  fadeFrom: number;
}

export interface BurstOptions {
  count?: number;
  /** atlas frame, e.g. vfx_bubble */
  frame?: string;
  tint?: number;
  speed?: number;
  gravity?: number;
  size?: number;
  life?: number;
  spread?: number;
  /** base direction in radians (default: all around) */
  angle?: number;
}

const MAX_PARTICLES = reducedMotion ? 60 : 260;

/**
 * Pooled particle system plus one-shot effects. All coordinates are in the
 * parent container's space (reference px). Sprites are reused, never created
 * per frame (§13: limit and reuse instances).
 */
export class Fx {
  readonly view = new Container();
  private readonly pool: Sprite[] = [];
  private readonly live: Particle[] = [];

  constructor() {
    Ticker.shared.add(this.update, this);
  }

  destroy(): void {
    Ticker.shared.remove(this.update, this);
    this.view.destroy({ children: true });
  }

  private take(tex: Texture): Sprite {
    const s = this.pool.pop() ?? new Sprite();
    s.texture = tex;
    s.anchor.set(0.5);
    s.alpha = 1;
    s.rotation = 0;
    s.tint = 0xffffff;
    s.visible = true;
    this.view.addChild(s);
    return s;
  }

  private update(t: Ticker): void {
    const dt = Math.min(t.deltaMS, 50) / 1000;
    for (let i = this.live.length - 1; i >= 0; i--) {
      const p = this.live[i];
      p.age += dt;
      const k = p.age / p.life;
      if (k >= 1) {
        p.s.visible = false;
        this.view.removeChild(p.s);
        this.pool.push(p.s);
        this.live.splice(i, 1);
        continue;
      }
      p.vx *= 1 - p.drag * dt;
      p.vy = p.vy * (1 - p.drag * dt) + p.gravity * dt;
      p.s.x += p.vx * dt;
      p.s.y += p.vy * dt;
      p.s.rotation += p.spin * dt;
      p.s.scale.set(p.scale0 + (p.scale1 - p.scale0) * k);
      p.s.alpha = k < p.fadeFrom ? 1 : 1 - (k - p.fadeFrom) / (1 - p.fadeFrom);
    }
  }

  /** Radial burst of small sprites. */
  burst(x: number, y: number, o: BurstOptions = {}): void {
    const tex = frame(o.frame ?? 'vfx_sparkle');
    const n = Math.min(reducedMotion ? Math.ceil((o.count ?? 6) / 3) : o.count ?? 6, MAX_PARTICLES - this.live.length);
    const size = (o.size ?? 28) / Math.max(tex.width, tex.height);
    for (let i = 0; i < n; i++) {
      const a = o.angle !== undefined ? o.angle + (Math.random() - 0.5) * (o.spread ?? 1) : Math.random() * Math.PI * 2;
      const v = (o.speed ?? 320) * (0.45 + Math.random() * 0.75);
      const s = this.take(tex);
      s.position.set(x + Math.cos(a) * 10, y + Math.sin(a) * 10);
      if (o.tint !== undefined) s.tint = o.tint;
      const sc = size * (0.7 + Math.random() * 0.6);
      this.live.push({
        s, vx: Math.cos(a) * v, vy: Math.sin(a) * v, gravity: o.gravity ?? 500, spin: (Math.random() - 0.5) * 6,
        drag: 2.2, life: (o.life ?? 0.55) * (0.8 + Math.random() * 0.4), age: 0, scale0: sc, scale1: sc * 0.35, fadeFrom: 0.55,
      });
    }
  }

  /** Expanding ring (§13: 300 ms, grow and fade). */
  shockwave(x: number, y: number, radius = 160, tint: number = COLORS.cream, duration: number = TIMING.shockwave): void {
    const s = this.take(frame('vfx_shockwave'));
    s.tint = tint;
    s.position.set(x, y);
    const base = 1 / s.texture.width;
    s.scale.set(base * radius * 0.4);
    gsap.to(s.scale, { x: base * radius * 2, y: base * radius * 2, duration: duration / 1000, ease: 'power2.out' });
    gsap.to(s, { alpha: 0, duration: duration / 1000, ease: 'power1.in', onComplete: () => this.release(s) });
  }

  private release(s: Sprite): void {
    gsap.killTweensOf(s);
    gsap.killTweensOf(s.scale);
    s.visible = false;
    this.view.removeChild(s);
    this.pool.push(s);
  }

  /** Light beam across a row or column (striped tile). */
  beam(x: number, y: number, horizontal: boolean, length: number, thickness: number, tint: number): void {
    const g = new Graphics().roundRect(-length / 2, -thickness / 2, length, thickness, thickness / 2).fill(tint);
    const core = new Graphics().roundRect(-length / 2, -thickness / 6, length, thickness / 3, thickness / 6).fill(COLORS.cream);
    const c = new Container();
    c.addChild(g, core);
    c.position.set(x, y);
    c.rotation = horizontal ? 0 : Math.PI / 2;
    c.scale.set(0.05, 1);
    this.view.addChild(c);
    gsap.timeline({ onComplete: () => c.destroy({ children: true }) })
      .to(c.scale, { x: 1, duration: 0.16, ease: 'power2.out' })
      .to(c.scale, { y: 0.1, duration: 0.22, ease: 'power2.in' })
      .to(c, { alpha: 0, duration: 0.22 }, '<');
  }

  /** Confetti from the top of an area, 800–1200 ms (§13). */
  confetti(left: number, width: number, top: number, count = 70): void {
    const frames = ['vfx_confetti_raspberry', 'vfx_confetti_butter', 'vfx_confetti_mint'];
    const n = Math.min(reducedMotion ? 15 : count, MAX_PARTICLES - this.live.length);
    for (let i = 0; i < n; i++) {
      const tex = frame(frames[i % 3]);
      const s = this.take(tex);
      s.position.set(left + Math.random() * width, top - Math.random() * 120);
      const sc = (22 + Math.random() * 14) / tex.height;
      this.live.push({
        s, vx: (Math.random() - 0.5) * 220, vy: 250 + Math.random() * 350, gravity: 420, spin: (Math.random() - 0.5) * 14,
        drag: 0.8, life: (TIMING.confetti / 1000) * (0.8 + Math.random() * 0.5), age: 0, scale0: sc, scale1: sc, fadeFrom: 0.75,
      });
    }
  }

  /** Confetti popping upwards from a point (combos). */
  confettiBurst(x: number, y: number, count = 24): void {
    const frames = ['vfx_confetti_raspberry', 'vfx_confetti_butter', 'vfx_confetti_mint'];
    for (let i = 0; i < 3; i++) {
      this.burst(x, y, { frame: frames[i], count: Math.ceil(count / 3), speed: 700, gravity: 1100, size: 26, life: 1, angle: -Math.PI / 2, spread: 2.2 });
    }
  }

  /** Floating praise word ("Sweet!", "Pawsome!"). */
  praise(text: string, x: number, y: number, size: number = FONT.size.celebration): void {
    const t = new Text({
      text,
      style: new TextStyle({
        fontFamily: FONT.family, fontSize: size, fontWeight: '700', fill: COLORS.cream,
        stroke: { color: COLORS.cocoa, width: 10, join: 'round' },
        dropShadow: { color: COLORS.raspberry, distance: 5, angle: Math.PI / 2, blur: 0, alpha: 1 },
      }),
    });
    t.anchor.set(0.5);
    t.position.set(x, y);
    t.scale.set(0.3);
    t.rotation = (Math.random() - 0.5) * 0.12;
    this.view.addChild(t);
    gsap.timeline({ onComplete: () => t.destroy() })
      .to(t.scale, { x: 1, y: 1, duration: 0.35, ease: 'back.out(2.5)' })
      .to(t, { y: y - 60, alpha: 0, duration: 0.45, ease: 'power1.in' }, '+=0.35');
  }

  /** Sprite that flies along a curve to a target (goal collection). */
  fly(tex: Texture, from: { x: number; y: number }, to: { x: number; y: number }, size: number, delay: number, onArrive: () => void): void {
    const s = this.take(tex);
    s.position.set(from.x, from.y);
    const sc = size / Math.max(tex.width, tex.height);
    s.scale.set(sc);
    const mid = { x: (from.x + to.x) / 2 + (Math.random() - 0.5) * 200, y: Math.min(from.y, to.y) - 80 };
    const p = { t: 0 };
    gsap.to(p, {
      t: 1, duration: 0.55, delay, ease: 'power1.in',
      onUpdate: () => {
        const u = 1 - p.t;
        s.x = u * u * from.x + 2 * u * p.t * mid.x + p.t * p.t * to.x;
        s.y = u * u * from.y + 2 * u * p.t * mid.y + p.t * p.t * to.y;
        s.scale.set(sc * (1 + 0.3 * Math.sin(p.t * Math.PI)) * (1 - 0.35 * p.t));
      },
      onComplete: () => {
        this.release(s);
        onArrive();
      },
    });
  }
}
